"use client";

import * as Sentry from "@sentry/nextjs";

import {
  AgentsZapierFacade as AgentsZapierFacadeImplementation,
  getFacadeErrorContext,
} from "@zapai/zapier-facade";

const removeTrailingSlash = (url: string) => {
  try {
    const urlObj = new URL(url); // Parse the URL
    if (urlObj.pathname.endsWith("/")) {
      urlObj.pathname = urlObj.pathname.slice(0, -1); // Remove trailing slash only from the path
    }
    return urlObj.toString();
  } catch (e) {
    console.error("Invalid URL:", url);
    throw e;
  }
};

export const AgentsZapierFacade = new AgentsZapierFacadeImplementation({
  baseUrl: "/api/zapier",

  /**
   * All of the URLs that we get from the Zapier API have trailing slashes,
   * but with Next.js/Vercel using a trailing slash results in a redirect to the same URL
   * without the trailing slash.
   *
   * This custom fetch implementation just removes the trailing slash if it exists.
   */
  fetch: async (input: Request | string, init?: RequestInit) => {
    let finalInput: Request | string;

    if (input instanceof Request) {
      const modifiedUrl = removeTrailingSlash(input.url);

      // Read the request body if necessary
      let body: BodyInit | null = null;
      if (input.bodyUsed) {
        console.warn(
          "Request body has already been used and cannot be reused.",
        );
      } else {
        body =
          input.method !== "GET" && input.method !== "HEAD"
            ? await input.text()
            : null;
      }

      finalInput = new Request(modifiedUrl, {
        method: input.method,
        headers: new Headers(input.headers), // Clone headers to avoid side effects
        body, // Use the extracted body
        mode: input.mode,
        credentials: input.credentials,
        cache: input.cache,
        redirect: input.redirect,
        referrer: input.referrer,
        referrerPolicy: input.referrerPolicy,
        integrity: input.integrity,
        keepalive: input.keepalive,
        signal: input.signal,
      });
    } else {
      finalInput = removeTrailingSlash(input);
    }

    return fetch(finalInput, init);
  },
  onError: async (error) => {
    Sentry.setContext("zapier-facade-error", {
      ...getFacadeErrorContext(error),
    });

    // for 4xx errors, we just capture events instead of exceptions
    if (error.response.status >= 400 && error.response.status < 500) {
      // TODO(NIK): add logger.warn
      Sentry.captureEvent({
        message: error.message,
        level: "warning",
      });
    } else {
      // TODO(NIK): add logger.error
      Sentry.captureException(error);
    }
  },
});
